import { Button, Progress, Tag } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ConfettiAnimation } from '../ConfettiAnimation';
import { TOTAL_ROUNDS } from './constants';
import { QuizPanel } from './styled';

const ResultHero = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 8px 0 4px 0;
    text-align: center;
    position: relative;

    h2 {
        margin: 0;
        font-size: clamp(32px, 5vw, 44px);
        font-weight: 700;
        line-height: 1.1;
    }

    p {
        margin: 0;
        color: #595959;
        font-size: 16px;
    }
`;

const ScoreRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    justify-content: center;
    align-items: center;
    margin: 8px 0 12px 0;
`;

const RoundsSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 12px;
`;

const RoundsHeading = styled.h3`
    margin: 0;
    text-align: center;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #8c8c8c;
`;

const RoundsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(${({ $count }) => $count}, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const RoundCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 18px 16px 18px;
    border: 1px solid ${({ $correct }) => ($correct ? '#b7eb8f' : '#ffccc7')};
    background: ${({ $correct }) =>
        $correct
            ? 'linear-gradient(135deg, #f6ffed 0%, #f0fbe3 100%)'
            : 'linear-gradient(135deg, #fff2f0 0%, #ffeae6 100%)'};
    border-radius: 12px;
    min-height: 140px;
    position: relative;
    transition: transform 0.15s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const RoundHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const RoundLabel = styled.span`
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #595959;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${({ $correct }) => ($correct ? '#52c41a' : '#ff4d4f')};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
`;

const RoundScore = styled.div`
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
    color: ${({ $correct }) => ($correct ? '#237804' : '#a8071a')};

    small {
        color: #8c8c8c;
        font-size: 14px;
        font-weight: 500;
        margin-left: 4px;
    }
`;

const RoundDetail = styled.div`
    font-size: 14px;
    color: #1f1f1f;
    line-height: 1.4;
    margin-top: auto;
`;

const Actions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    padding: 16px 0 4px 0;
`;

const getVerdictText = percent => {
    if (percent >= 80) return 'Vynikající!';
    if (percent >= 60) return 'Skvělé!';
    if (percent >= 40) return 'Dobré';
    if (percent >= 20) return 'Jde to';
    return 'Příště to bude lepší';
};

const getVerdictEmoji = percent => {
    if (percent >= 80) return '🏆';
    if (percent >= 60) return '🎯';
    if (percent >= 40) return '👍';
    if (percent >= 20) return '🤔';
    return '😅';
};

export const QuizFinalResult = ({
    score,
    totalRounds = TOTAL_ROUNDS,
    scorePerRound = 100,
    rounds = [],
    onRestart,
}) => {
    const maxScore = totalRounds * scorePerRound;
    const percent = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const showConfetti = percent >= 70;

    return (
        <QuizPanel>
            <ResultHero>
                {showConfetti && <ConfettiAnimation />}
                <Tag color="green">Hotovo</Tag>
                <h2>
                    {score}/{maxScore} bodů
                </h2>
                <p>
                    {getVerdictEmoji(percent)} {getVerdictText(percent)}
                </p>
            </ResultHero>

            <ScoreRow>
                <Progress
                    type="circle"
                    percent={percent}
                    strokeColor={{ '0%': '#c80707', '100%': '#a00606' }}
                    width={140}
                />
            </ScoreRow>

            {rounds.length > 0 && (
                <RoundsSection>
                    <RoundsHeading>Tvé tipy v jednotlivých kolech</RoundsHeading>
                    <RoundsGrid $count={rounds.length}>
                        {rounds.map((r, i) => (
                            <RoundCard key={i} $correct={r.correct}>
                                <RoundHead>
                                    <RoundLabel>Kolo {i + 1}</RoundLabel>
                                    <StatusBadge $correct={r.correct}>{r.correct ? '✓' : '✗'}</StatusBadge>
                                </RoundHead>
                                <RoundScore $correct={r.correct}>
                                    {r.score}
                                    <small>/ {scorePerRound}</small>
                                </RoundScore>
                                {r.label && <RoundDetail>{r.label}</RoundDetail>}
                            </RoundCard>
                        ))}
                    </RoundsGrid>
                </RoundsSection>
            )}

            <Actions>
                <Button type="primary" size="large" onClick={onRestart}>
                    Hrát znovu
                </Button>
                <Link to="/">
                    <Button size="large">Zpět na výběr herního módu</Button>
                </Link>
            </Actions>
        </QuizPanel>
    );
};
